import React, { useMemo } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { Box, Typography } from '@mui/material'
import { ChargingSessionExportFormatDto } from '../api/dto/charging-session-export-request.dto'
import { AppLang } from '@e3dc-react/shell/State/App/AppModel'
import { TFunction } from 'i18next'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { TextField } from '@e3dc-react/shell/Elements/InputFields'

export interface ReimbursementExportSessionsDialogProps {
  templateLanguage: AppLang | undefined
  handleExportFile: (format: ChargingSessionExportFormatDto, availableLanguage: AppLang | undefined, reimbursementPrice?: number) => void
  currency: string | undefined
  open: boolean
  onClose: () => void
  place: number
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createReimbursementPriceSchema = (t: TFunction) =>
  z.object({
    reimbursementPrice: z
      .string({ required_error: t('emobility.tariffs.priceRequired') })
      .min(1, t('emobility.tariffs.priceRequired'))
      .refine(value => !Number.isNaN(Number(value)), {
        message: t('emobility.tariffs.mustBeNumber'),
      }),
  })

export type ReimbursementPriceFormData = z.infer<ReturnType<typeof createReimbursementPriceSchema>>

/**
 * Dialog component to allow user to enter reimbursement price for export charging sessions file.
 */
export const ReimbursementExportSessionsDialog: React.FC<ReimbursementExportSessionsDialogProps> = ({
  open,
  place,
  onClose,
  handleExportFile,
  templateLanguage,
  currency,
}) => {
  const { t } = useCustomTranslation()

  const reimbursementPriceSchema = useMemo(() => createReimbursementPriceSchema(t), [t])

  const form = useForm<ReimbursementPriceFormData>({
    resolver: zodResolver(reimbursementPriceSchema),
    defaultValues: {
      reimbursementPrice: '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const {
    control,
    formState: { isDirty, isValid, errors },
    getValues,
  } = form

  const handleSubmit = async (): Promise<void> => {
    const formData = getValues()
    onClose()
    handleExportFile(ChargingSessionExportFormatDto.PdfReimbursement, templateLanguage, Number(formData.reimbursementPrice))
  }

  return (
    <Dialog
      maxWidth={'sm'}
      title={t('emobility.chargingSession.pdf-reimbursement-title')}
      onClose={onClose}
      place={place}
      actions={{
        primary: {
          name: t('emobility.chargingSession.export'),
          onClick: handleSubmit,
          disabled: !isDirty || !isValid,
        },
        secondary: {
          name: t('common.cancel'),
        },
      }}
    >
      <Typography>{t('emobility.chargingSession.pdf-reimbursement-content')}</Typography>

      <Box mt={3}>
        <Controller
          name="reimbursementPrice"
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <TextField
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              label={t('emobility.chargingSession.reimbursement-price-field', { currency })}
              helperText={errors.reimbursementPrice?.message}
              required
              clearable={false}
              type="number"
              step={0.1}
            />
          )}
        />
      </Box>
    </Dialog>
  )
}
